import { useMemo, useRef, useState } from 'react';
import {
  Button,
  ComposedModal,
  InlineLoading,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextArea,
} from '@carbon/react';
import { ApiError, api, MappingKind } from '../lib/api';
import { useToast } from '../components/ui';

interface ImportRow {
  kind: MappingKind;
  sourceCode: string;
  dhis2Uid: string;
  name?: string | null;
  facilityId?: number | null;
  enabled?: boolean;
}

interface ImportResult {
  imported: number;
  created: number;
  updated: number;
}

const EXAMPLE = `{
  "mappings": [
    { "kind": "element", "sourceCode": "NCD_001_OPD", "dhis2Uid": "BtFmIGykldh", "name": "Sickle cell anaemia (OPD)" },
    { "kind": "combo",   "sourceCode": "MALE_UNDER_5", "dhis2Uid": "HDPff2ZKsJe", "name": "Under 5, Male" }
  ]
}`;

/**
 * Loads many mappings at once from a pasted or uploaded JSON file.
 *
 * A national data set is a few hundred data elements; entering those one at a time through
 * {@link MappingModal} is not something anyone will do accurately, and the list normally already
 * exists somewhere - a DHIS2 metadata export, or a name-to-UID mapping worked out by hand. This
 * takes that list as it is, in the exact shape `/api/mappings/import` accepts, and shows nothing as
 * imported unless every row was accepted: a half-loaded mapping table would silently start
 * translating some of a data set's elements and not others.
 */
export function ImportMappingsModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[] | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const parsed = useMemo(() => parseInput(text), [text]);

  function loadFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result ?? ''));
    reader.readAsText(file);
  }

  async function submit() {
    if (!parsed.rows) {
      return;
    }
    setBusy(true);
    setProblems(null);
    try {
      const response = await api.post<ImportResult>('/api/mappings/import', { mappings: parsed.rows });
      setResult(response);
      toast(
        'ok',
        `Imported ${response.imported} mapping${response.imported === 1 ? '' : 's'} ` +
          `(${response.created} new, ${response.updated} updated)`,
      );
      onImported();
    } catch (error) {
      if (error instanceof ApiError && isProblemList((error.body as { problems?: unknown })?.problems)) {
        setProblems((error.body as { problems: string[] }).problems);
      } else {
        toast('error', error instanceof Error ? error.message : String(error));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ComposedModal open onClose={onClose} size="lg" className="mappings-modal">
      <ModalHeader title="Import mappings" closeModal={onClose} />
      <ModalBody>
        <div className="mappings-modal-form">
          <p className="muted">
            Paste or upload a <code className="mono">{'{ "mappings": [...] }'}</code> file — the same shape
            produced when generating a data set's mappings. Nothing is written unless every row is valid;
            a row that already exists (same kind, source code and scope) is updated rather than duplicated,
            so re-running a corrected file is safe.
          </p>

          <div className="row" style={{ gap: 8 }}>
            <Button kind="tertiary" size="sm" onClick={() => fileInput.current?.click()}>
              Load from file…
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json,.txt"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  loadFile(file);
                }
                event.target.value = '';
              }}
            />
            {parsed.rows ? (
              <span className="faint small">
                {parsed.rows.length} mapping{parsed.rows.length === 1 ? '' : 's'} ready ·{' '}
                {summariseKinds(parsed.rows)}
              </span>
            ) : null}
          </div>

          <TextArea
            id="import-mappings-textarea"
            labelText="Mappings JSON"
            placeholder={EXAMPLE}
            rows={14}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setProblems(null);
              setResult(null);
            }}
            className="mono"
          />

          {text.trim() && parsed.error ? (
            <InlineNotification kind="error" lowContrast hideCloseButton title="Could not read this" subtitle={parsed.error} />
          ) : null}

          {problems ? (
            <>
              <InlineNotification
                kind="error"
                lowContrast
                hideCloseButton
                title="Nothing was imported"
                subtitle={`${problems.length} problem${problems.length === 1 ? '' : 's'} found — fix these and try again.`}
              />
              <div className="stack mono small" style={{ gap: 2, maxHeight: 180, overflowY: 'auto', padding: '4px 0' }}>
                {problems.map((problem, index) => (
                  <div key={index}>{problem}</div>
                ))}
              </div>
            </>
          ) : null}

          {result ? (
            <InlineNotification
              kind="success"
              lowContrast
              hideCloseButton
              title="Imported"
              subtitle={`${result.imported} mapping${result.imported === 1 ? '' : 's'} — ${result.created} new, ${result.updated} updated.`}
            />
          ) : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        <Button kind="primary" onClick={submit} disabled={busy || !parsed.rows}>
          {busy ? <InlineLoading status="active" description="" /> : null}
          Import{parsed.rows ? ` ${parsed.rows.length}` : ''}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}

/** Parses and lightly checks the pasted text, mirroring what the server itself will insist on. */
function parseInput(text: string): { rows: ImportRow[] | null; error: string | null } {
  const trimmed = text.trim();
  if (!trimmed) {
    return { rows: null, error: null };
  }
  let body: unknown;
  try {
    body = JSON.parse(trimmed);
  } catch (error) {
    return { rows: null, error: error instanceof Error ? error.message : 'Invalid JSON' };
  }
  const rows = (body as { mappings?: unknown })?.mappings;
  if (!Array.isArray(rows)) {
    return { rows: null, error: 'Expected a top-level "mappings" array.' };
  }
  if (rows.length === 0) {
    return { rows: null, error: 'The mappings array is empty.' };
  }
  return { rows: rows as ImportRow[], error: null };
}

function summariseKinds(rows: ImportRow[]): string {
  const counts = new Map<string, number>();
  for (const row of rows) {
    counts.set(row.kind, (counts.get(row.kind) ?? 0) + 1);
  }
  return [...counts.entries()].map(([kind, count]) => `${count} ${kind}`).join(', ');
}

function isProblemList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}
